import type { ProviderSkillInvocation } from "@afar-research/contracts";

import { splitLeadingComposerSkillLines } from "./composerSkillToken.ts";

/** A Provider Skill selection bound to the exact token it will be dispatched for. */
export interface DispatchedProviderSkill extends ProviderSkillInvocation {
  readonly promptStart: number;
  readonly promptEnd: number;
}

/**
 * A Provider Skill selection with nowhere left to anchor in the outgoing prompt.
 * The caller reports it: a selection is never discarded in silence.
 */
export interface DroppedProviderSkill {
  readonly name: string;
  readonly path: string;
}

export interface ProviderSkillDispatchPlan {
  /** Selections to hand the provider. Ranges are exact against `outgoingPrompt`. */
  readonly dispatched: ReadonlyArray<DispatchedProviderSkill>;
  readonly dropped: ReadonlyArray<DroppedProviderSkill>;
  /**
   * The prompt to send, with the dispatched tokens still in place: an adapter that
   * rewrites them (Claude Code builds a slash command) needs them, and one that
   * does not (Codex) sends `promptWithoutSkillTokens` instead.
   */
  readonly outgoingPrompt: string | undefined;
  /** `outgoingPrompt` with the dispatched tokens removed. */
  readonly promptWithoutSkillTokens: string | undefined;
}

interface TokenRange {
  readonly start: number;
  readonly end: number;
}

const tokenPattern = (name: string) =>
  new RegExp(`(^|\\s)\\$${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\s|$)`, "gu");

function bodyStartOf(prompt: string): number {
  return prompt.length - splitLeadingComposerSkillLines(prompt).body.join("\n").length;
}

/** Remove exact token ranges, taking one adjacent separator with each. */
function removeTokens(prompt: string, ranges: ReadonlyArray<TokenRange>): string {
  let result = prompt;
  // Descending, so each splice leaves the ranges still to come unmoved.
  for (const range of [...ranges].sort((left, right) => right.start - left.start)) {
    // Take one separator with the token so removing a chip leaves neither a
    // double space nor a stray blank line behind.
    const end = /\s/u.test(prompt[range.end] ?? "") ? range.end + 1 : range.end;
    const start =
      end === range.end && /\s/u.test(prompt[range.start - 1] ?? "")
        ? range.start - 1
        : range.start;
    result = `${result.slice(0, start)}${result.slice(end)}`;
  }
  return result;
}

function emptyToUndefined(prompt: string): string | undefined {
  return prompt.length > 0 ? prompt : undefined;
}

/**
 * Resolve every Provider Skill selection against the prompt actually being sent.
 *
 * This is the one rule for turning composer selections into provider-native skill
 * dispatch. Every caller uses it, so a token is stripped from the prompt exactly
 * when its selection is dispatched, and a selection that cannot be honoured is
 * handed back for the caller to report rather than vanishing.
 *
 * The recorded `promptStart`/`promptEnd` range is a hint, not the selection's
 * identity: prompts are rewritten between the composer and the provider (guided
 * first-turn chips, terminal context expansion, trimming, Thread Queue
 * substitution), and an offset that no longer lines up is not evidence that the
 * user changed their mind. Pass `sourcePrompt` when the prompt was rewritten, so a
 * selection keeps the side of the leading chip block it was made on.
 */
export function planProviderSkillDispatch(input: {
  readonly prompt: string | undefined;
  readonly sourcePrompt?: string | undefined;
  readonly skillInvocations: ReadonlyArray<ProviderSkillInvocation>;
}): ProviderSkillDispatchPlan {
  const outgoing = input.prompt?.trim() ?? "";
  const bodyStart = bodyStartOf(outgoing);
  const sourceBodyStart =
    input.sourcePrompt === undefined ? undefined : bodyStartOf(input.sourcePrompt);

  const claimed = new Set<number>();
  const dispatched: DispatchedProviderSkill[] = [];
  const dropped: DroppedProviderSkill[] = [];

  for (const skill of input.skillInvocations) {
    const occurrences = [...outgoing.matchAll(tokenPattern(skill.name))]
      .map((match) => (match.index ?? 0) + (match[1]?.length ?? 0))
      .filter((start) => !claimed.has(start));
    // The recorded range wins whenever it still reads this skill's own token.
    const exact =
      skill.promptStart !== undefined && occurrences.includes(skill.promptStart)
        ? skill.promptStart
        : undefined;
    // A rewrite moved the token: keep the selection on the side of the leading
    // chip block the user made it on, so same-name chips do not swap places.
    const wasInSourceBody =
      sourceBodyStart !== undefined && skill.promptStart !== undefined
        ? skill.promptStart >= sourceBodyStart
        : undefined;
    const preferred =
      wasInSourceBody === undefined
        ? undefined
        : occurrences.find((start) => start >= bodyStart === wasInSourceBody);
    const start = exact ?? preferred ?? occurrences[0];
    if (start === undefined) {
      dropped.push({ name: skill.name, path: skill.path });
      continue;
    }
    claimed.add(start);
    dispatched.push({ ...skill, promptStart: start, promptEnd: start + skill.name.length + 1 });
  }

  return {
    dispatched,
    dropped,
    outgoingPrompt: emptyToUndefined(outgoing),
    promptWithoutSkillTokens: emptyToUndefined(
      removeTokens(
        outgoing,
        dispatched.map((skill) => ({ start: skill.promptStart, end: skill.promptEnd })),
      ).trim(),
    ),
  };
}
